<template>
  <div v-if="isProjectReady" class="document">
    <DocumentPrintPage
      v-for="(page, index) in pages"
      :key="page.id"
      :fields="getFieldsByPage(index)"
      :project="project"
      :image="page.path"
    />
  </div>
  <div v-else>Keine Daten vorhanden!</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DocumentPrintPage from '@/components/Document/DocumentPrintPage';
import { ExtendedProject } from '@/helpers/projects';
import {
  formatSolarPlantTotalPanelPower,
  formatInverterTotalNominalPower,
} from '@/helpers/solarPlant';

export default {
  name: 'ProjectDocumentPrintPage',
  components: {
    DocumentPrintPage,
  },
  props: {
    projectNumber: {
      required: true,
      default: null,
    },
    documentNumber: {
      required: true,
      default: null,
    },
  },
  computed: {
    ...mapGetters('auth', ['isAdmin']),
    ...mapGetters(['getProjectByNumber', 'getDocumentByNumber']),
    document() {
      return this.getDocumentByNumber(this.documentNumber);
    },
    project() {
      const project = this.getProjectByNumber(this.number);
      if (project) {
        new ExtendedProject(project);
      }
      return project;
    },
    pages() {
      return this.document.pageBackgroundAttachments;
    },
    /** Check prerequesites to generate pdf */
    isProjectReady() {
      return this.project && this.document && this.document.pageBackgroundAttachments.length > 0;
    },
  },
  methods: {
    ...mapActions(['fetchProjectByNumber', 'fetchDocumentByNumber']),
    getFieldsByPage(index) {
      const page = index + 1;
      const pageFields = this.document.fields.filter((field) => field.page == page);
      const expandedPageFields = this.expandPageFields(pageFields);
      return expandedPageFields;
    },
    expandPageFields(pageFields) {
      const expandedPageFields = [];
      pageFields.forEach((field) => {
        if (field.key === 'list_inverters') {
          expandedPageFields.push(...this.expandInverterList(field));
        } else if (field.key === 'list_panels') {
          expandedPageFields.push(...this.expandSolarPanelList(field));
        } else {
          expandedPageFields.push(field);
        }
      });
      return expandedPageFields;
    },
    expandInverterList(templateField) {
      const inverters = this.project?.solarPlant?.inverters || [];
      const rowHeight = templateField.height || 18;
      const titleField = {
        ...templateField,
        key: `${templateField.key}_title`,
        name: 'common.freeText',
        value: this.$t('inverters'),
      };

      const inverterFields = inverters.map((inverter, index) => {
        const manufacturer = inverter?.item?.inverter?.manufacturer || '';
        const type = inverter?.item?.inverter?.type || '';
        const nominalPower = inverter?.item?.inverter?.nominalPower;
        const inverterCount = parseInt(inverter?.count || 1, 10);
        const inverterLabel = [manufacturer, type].filter(Boolean).join(' - ') || '-';
        const nominalPowerLabel =
          nominalPower !== null && nominalPower !== undefined ? ` (${nominalPower} kW)` : '';
        const value =
          inverterCount > 1
            ? `${inverterCount}x ${inverterLabel}${nominalPowerLabel}`
            : `${inverterLabel}${nominalPowerLabel}`;

        return {
          ...templateField,
          key: `${templateField.key}_${index}`,
          name: 'common.freeText',
          value,
          y: templateField.y + (index + 1) * rowHeight,
        };
      });

      const totalPowerField = {
        ...templateField,
        key: `${templateField.key}_total_power`,
        name: 'common.freeText',
        value: `${this.$t('inverter')} ${this.$t('totalPower')}: ${formatInverterTotalNominalPower(
          inverters
        )} kW`,
        y: templateField.y + (inverterFields.length + 1) * rowHeight,
      };

      return [titleField, ...inverterFields, totalPowerField];
    },
    expandSolarPanelList(templateField) {
      const solarPanels = this.project?.solarPlant?.solarPanels || [];
      const rowHeight = templateField.height || 18;
      const titleField = {
        ...templateField,
        key: `${templateField.key}_title`,
        name: 'common.freeText',
        value: this.$t('solarPanels'),
      };

      const solarPanelFields = solarPanels.map((solarPanel, index) => {
        const manufacturer = solarPanel?.item?.solarPanel?.manufacturer || '';
        const type = solarPanel?.item?.solarPanel?.type || '';
        const panelPower = solarPanel?.item?.solarPanel?.power;
        const panelCount = parseInt(solarPanel?.count || 1, 10);
        const solarPanelLabel = [manufacturer, type].filter(Boolean).join(' - ') || '-';
        const panelPowerLabel =
          panelPower !== null && panelPower !== undefined ? ` (${panelPower} Wp)` : '';
        const value =
          panelCount > 1
            ? `${panelCount}x ${solarPanelLabel}${panelPowerLabel}`
            : `${solarPanelLabel}${panelPowerLabel}`;

        return {
          ...templateField,
          key: `${templateField.key}_${index}`,
          name: 'common.freeText',
          value,
          y: templateField.y + (index + 1) * rowHeight,
        };
      });

      const totalPowerField = {
        ...templateField,
        key: `${templateField.key}_total_power`,
        name: 'common.freeText',
        value: `${this.$t('solarPanel')} ${this.$t(
          'totalPower'
        )}: ${formatSolarPlantTotalPanelPower(solarPanels)} kWp`,
        y: templateField.y + (solarPanelFields.length + 1) * rowHeight,
      };

      return [titleField, ...solarPanelFields, totalPowerField];
    },
  },
  async mounted() {
    this.number = this.projectNumber;
    await this.fetchProjectByNumber(this.number);
    await this.fetchDocumentByNumber(this.documentNumber);
  },
};
</script>
<style lang="scss" scoped>
.document {
  position: absolute;
  width: 100%;
  height: 100%;
}
</style>
